import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import {
  C,
  fmtClass,
  fmtSection,
  Icon,
  Btn,
  Badge,
  TH,
  TD,
  Field,
  selectStyle,
} from "./feeUI";
import {
  classBelongsToSession,
  classSectionsOf,
  sectionsForClassSession,
} from "../../../utils/campusClasses";

const FeeSetupView = ({ students, feeAssignments, onConfigure, campusId }) => {
  const [sessionId, setSessionId] = useState("");
  const [cls, setCls] = useState("all");
  const [section, setSection] = useState("all");
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const scope = campusId ? `?campus_id=${campusId}` : "";
        const [sessionsRes, classesRes, campusesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/sessions${scope}`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE_URL}/api/classes${scope}`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE_URL}/api/campuses`, { headers: getAuthHeaders() }),
        ]);

        if (!sessionsRes.ok || !classesRes.ok || !campusesRes.ok) {
          throw new Error("Could not load sessions, classes, and campuses.");
        }

        const [sessionsData, classesData, campusesData] = await Promise.all([
          sessionsRes.json(),
          classesRes.json(),
          campusesRes.json(),
        ]);

        if (!cancelled) {
          setSessions(Array.isArray(sessionsData) ? sessionsData : []);
          setClasses(Array.isArray(classesData) ? classesData : []);
          setCampuses(Array.isArray(campusesData) ? campusesData : []);
        }
      } catch (error) {
        console.error("Failed to load fee setup options", error);
        if (!cancelled) {
          setSessions([]);
          setClasses([]);
          setCampuses([]);
        }
      } finally {
        if (!cancelled) setIsLoadingOptions(false);
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [campusId]);

  const normalize = (value) => String(value || "").trim().toLowerCase();

  const selectedSession = useMemo(
    () =>
      sessions.find((session) => String(session.id) === String(sessionId)) ||
      null,
    [sessions, sessionId],
  );
  const sessionName = selectedSession?.name || "";

  const sessionClasses = useMemo(
    () =>
      sessionId
        ? classes.filter((classItem) => classBelongsToSession(classItem, sessionId))
        : [],
    [classes, sessionId],
  );

  const sessionStudents = useMemo(() => {
    if (!sessionId) return [];

    const bySessionId = students.filter(
      (student) => String(student.academic_session_id || "") === String(sessionId),
    );

    if (bySessionId.length > 0) return bySessionId;

    return sessionName
      ? students.filter(
          (student) =>
            normalize(student.academic_session) === normalize(sessionName),
        )
      : [];
  }, [students, sessionId, sessionName]);

  useEffect(() => {
    setCls("all");
    setSection("all");
  }, [sessionId]);

  useEffect(() => {
    setSection("all");
  }, [cls]);

  const availableSections = useMemo(() => {
    if (!sessionId) return [];

    const selectedClass =
      cls === "all"
        ? null
        : sessionClasses.find((classItem) => String(classItem.id) === String(cls)) ||
          null;

    const fromSetup = sectionsForClassSession(sessionClasses, {
      sessionId,
      className: selectedClass?.name || "",
      formatSection: fmtSection,
    });

    if (fromSetup.length) return fromSetup;

    const sectionSet = new Set();
    sessionStudents.forEach((student) => {
      if (selectedClass) {
        const matchesClass =
          (student.class_id &&
            String(student.class_id) === String(selectedClass.id)) ||
          (!student.class_id && student.class === selectedClass.name);
        if (!matchesClass) return;
      }
      const sectionValue = fmtSection(student.section);
      if (sectionValue && sectionValue !== "—") sectionSet.add(sectionValue);
    });

    return Array.from(sectionSet).sort();
  }, [sessionClasses, sessionStudents, sessionId, cls]);

  const rows = useMemo(() => {
    const wanted =
      cls === "all"
        ? sessionClasses
        : sessionClasses.filter(
            (classItem) => String(classItem.id) === String(cls),
          );

    return wanted.map((classItem) => {
      let classStudents = sessionStudents.filter(
        (student) =>
          (student.class_id &&
            String(student.class_id) === String(classItem.id)) ||
          (!student.class_id && student.class === classItem.name),
      );

      if (section !== "all") {
        classStudents = classStudents.filter(
          (student) => fmtSection(student.section) === section,
        );
      }

      const ids = classStudents.map((student) => student.id);

      const sections = [
        ...new Set(
          [
            ...classSectionsOf(classItem, fmtSection),
            ...classStudents.map((student) => fmtSection(student.section)),
          ].filter((sectionVal) => sectionVal && sectionVal !== "—"),
        ),
      ].sort();

      const feeKeys = new Set();
      classStudents.forEach((student) => {
        const cats = feeAssignments[String(student.id)] || {};
        Object.values(cats).forEach((assignment) => {
          if ((assignment.session || "") === sessionName) {
            feeKeys.add(`${assignment.categoryId}_${assignment.month}`);
          }
        });
      });

      return {
        id: classItem.id,
        cls: classItem.name,
        count: classStudents.length,
        ids,
        sections,
        feesCount: feeKeys.size,
      };
    });
  }, [
    cls,
    section,
    feeAssignments,
    sessionClasses,
    sessionName,
    sessionStudents,
  ]);

  const styles = {
    page: {
      minHeight: "100vh",
      background: "#eef5ff",
      padding: "0px 16px 24px",
    },
    container: {
      maxWidth: "1160px",
      margin: "0 auto",
    },
    cardWrap: {
      background: "#fff",
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
      border: "1px solid #dbe7f5",
    },
   
    content: {
      padding: "22px 22px 24px",
    },
    intro: {
      fontSize: "13px",
      color: "#475569",
      margin: "0 0 18px",
      lineHeight: 1.6,
      maxWidth: "780px",
    },
    filters: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: "16px",
      width: "100%",
    },
    tableWrap: {
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "760px",
    },
    empty: {
      padding: "28px 8px 8px",
      textAlign: "center",
      color: "#64748b",
      fontSize: "13px",
    },
    headerTitle: {
      fontSize: "16px",
      fontWeight: 700,
      color: "#0f172a",
    },
    formBlock: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    sectionSpacer: {
      marginTop: "22px",
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.cardWrap}>
          <div style={styles.topBorder} />
          <div style={styles.content}>
            <div style={styles.formBlock}>
              

              <div style={styles.filters}>
                <Field label="Academic Session *">
                  <select
                    style={selectStyle}
                    value={sessionId}
                    disabled={isLoadingOptions}
                    onChange={(event) => setSessionId(event.target.value)}
                  >
                    <option value="">
                      {isLoadingOptions ? "Loading sessions..." : "Select session..."}
                    </option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Class">
                  <select
                    style={{
                      ...selectStyle,
                      opacity: sessionId ? 1 : 0.55,
                      cursor: sessionId ? "pointer" : "not-allowed",
                    }}
                    value={cls}
                    disabled={!sessionId || isLoadingOptions}
                    onChange={(event) => setCls(event.target.value)}
                  >
                    <option value="all">All Classes</option>
                    {sessionClasses.map((classItem) => (
                      <option key={classItem.id} value={classItem.id}>
                        {fmtClass(classItem.name)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Section">
                  <select
                    style={{
                      ...selectStyle,
                      opacity: sessionId ? 1 : 0.55,
                      cursor: sessionId ? "pointer" : "not-allowed",
                    }}
                    value={section}
                    disabled={!sessionId || isLoadingOptions}
                    onChange={(event) => setSection(event.target.value)}
                  >
                    <option value="all">All Sections</option>
                    {availableSections.map((sec) => (
                      <option key={sec} value={sec}>
                        {sec}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            {sessionId && (
              <div style={styles.sectionSpacer}>
                <div style={styles.topRow}>
                  <div style={styles.headerTitle}>Classes in {sessionName}</div>
                  <Badge color="slate">
                    {rows.length} class{rows.length === 1 ? "" : "es"}
                  </Badge>
                </div>

                {rows.length === 0 ? (
                  <div style={styles.empty}>
                    No classes found for this academic session.
                  </div>
                ) : (
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={{ background: "#f8fbff" }}>
                          <TH>Class</TH>
                          <TH>Sections</TH>
                          <TH right>Students</TH>
                          <TH right>Fees Set Up</TH>
                          <TH>Actions</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr
                            key={row.id}
                            style={{
                              background: index % 2 === 0 ? "#ffffff" : "#f8fbff",
                            }}
                          >
                            <TD>
                              <Badge color="blue">{fmtClass(row.cls)}</Badge>
                            </TD>
                            <TD>{row.sections.length ? row.sections.join(", ") : "—"}</TD>
                            <TD right>{row.count}</TD>
                            <TD right>
                              {row.feesCount > 0 ? (
                                <Badge color="amber">{row.feesCount}</Badge>
                              ) : (
                                <span style={{ color: "#94a3b8" }}>None</span>
                              )}
                            </TD>
                            <TD>
                              <Btn
                                size="sm"
                                disabled={row.count === 0}
                                onClick={() => onConfigure(row.cls, sessionName, row.ids)}
                              >
                                <Icon.Plus /> Set Up Fee
                              </Btn>
                            </TD>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {!sessionId && (
              <div style={styles.empty}>
                Select an academic session above to see its classes.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeSetupView;